import React from 'react';
import PropTypes from 'prop-types';

import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import { connect } from 'react-redux';
import { compose } from 'redux';
import messages from './messages';
import {
  DISTANCE_YEARS_RANGE,
  DURATION_RANGE,
  MONTHLY_RATE_RANGE,
  DEPOSIT_RANGE,
} from 'containers/App/constants';

import { createStructuredSelector } from 'reselect';
import reducer from './reducer';
import saga from './saga';

import {
  loadDeliveryTimes,
  loadBrokers,
  loadProducts,
  setMonthlyRate,
  setDeposit,
  setDistance,
  setNotes,
  setPrivateNotes,
  setDuration,
  setRightLabel,
  setFastDelivery,
  setDeliveryTime,
  setBroker,
  setProduct,
  setReference,
  hasError,
} from './actions';

import {
  makeSelectDeposit,
  makeSelectDistance,
  makeSelectMonthlyRate,
  makeSelectDuration,
  makeSelectRightLabel,
  makeSelectFastDelivery,
  makeSelectDeliveryTime,
  makeSelectDeliveryTimes,
  makeSelectBrokers,
  makeSelectBroker,
  makeSelectProducts,
  makeSelectProduct,
  makeSelectNotes,
  makeSelectPrivateNotes,
  makeSelectReference,
} from './selectors';

import { Form, Input, Select, Checkbox } from 'antd';
import './styles.scss';

import { isInputNumberValid } from 'containers/App/utils/validator';

import configs from 'configs';

class OfferValue extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      monthlyRateError: false,
      depositError: false,
      durationError: false,
      distanceError: false,
      brokerError: false,
      duration: DURATION_RANGE.min * 3,
      distance: (DISTANCE_YEARS_RANGE.min / 1000) * 15,
    };

    this.handleOnMonthlyRateChange = this.handleOnMonthlyRateChange.bind(this);
    this.handleOnDepositChange = this.handleOnDepositChange.bind(this);
    this.handleOnDurationChange = this.handleOnDurationChange.bind(this);
    this.handleOnDistanceChange = this.handleOnDistanceChange.bind(this);
    this.handleOnRightLabelChange = this.handleOnRightLabelChange.bind(this);
    this.handleOnDeliveryTimeChange = this.handleOnDeliveryTimeChange.bind(this);
    this.handleOnBrokerChange = this.handleOnBrokerChange.bind(this);
    this.handleOnProductChange = this.handleOnProductChange.bind(this);
    this.handleOnFastDeliveryChange = this.handleOnFastDeliveryChange.bind(this);
    this.handleOnNotesChange = this.handleOnNotesChange.bind(this);
    this.handleOnPrivateNotesChange = this.handleOnPrivateNotesChange.bind(this);
    this.handleOnReferenceChange = this.handleOnReferenceChange.bind(this);
  }

  componentDidMount() {
    this.props.loadDeliveryTimes();
    this.props.loadBrokers();
    this.props.loadProducts();
  }

  handleOnMonthlyRateChange({target: {value}}) {
    const monthlyRateError = isInputNumberValid(
      value,
      MONTHLY_RATE_RANGE.min,
      MONTHLY_RATE_RANGE.max,
    );
    this.setState({ monthlyRateError }, () => {
      this.props.setMonthlyRate(parseInt(value));
      this.validateInputs();
    });
  }

  handleOnDepositChange({target: {value}}) {
    const depositError = isInputNumberValid(
      value,
      DEPOSIT_RANGE.min,
      DEPOSIT_RANGE.max,
    );
    this.setState({ depositError }, () => {
      this.props.setDeposit(parseInt(value));
      this.validateInputs();
    });
  }

  handleOnDurationChange({target: {value}}) {
    const durationError = isInputNumberValid(
      value,
      DURATION_RANGE.min,
      DURATION_RANGE.max,
    );
    this.setState({ durationError }, () => {
      this.props.setDuration(parseInt(value));
      this.validateInputs();
    });
  }

  handleOnDistanceChange({target: {value}}) {
    this.props.setDistance(parseInt(value));
    const distanceError = isInputNumberValid(
      value,
      DISTANCE_YEARS_RANGE.min,
      DISTANCE_YEARS_RANGE.max,
    );
    this.setState({ distanceError }, () => {
      this.props.setDistance(parseInt(value));
      this.validateInputs();
    });
  }

  handleOnNotesChange({target: {value}}) {
    this.props.setNotes(value);
  }

  handleOnPrivateNotesChange({target: {value}}) {
    this.props.setPrivateNotes(value);
  }

  handleOnRightLabelChange(value) {
    this.props.setRightLabel(value, configs.tags[value]);
  }

  handleOnDeliveryTimeChange(value) {
    this.props.setDeliveryTime(value);
  }

  handleOnBrokerChange(value) {
    this.props.setBroker(value);
  }

  handleOnProductChange(value) {
    this.props.setProduct(value);
  }

  handleOnFastDeliveryChange({target: {checked}}) {
    this.props.setFastDelivery(checked);
  }

  handleOnReferenceChange({target: {value}}) {
    this.props.setReference(value);
  }

  validateInputs() {
    if (
      !this.state.monthlyRateError &&
      !this.state.depositError &&
      !this.state.durationError &&
      !this.state.distanceError
    ) {
      this.props.hasError(false);
    } else {
      this.props.hasError(true);
    }
  }

  render() {
    const {
      monthlyRate,
      distance,
      duration,
      deposit,
      right_label,
      fastDelivery,
      deliveryTime,
      deliveryTimes,
      brokers,
      products,
      broker,
      product,
      referenceCode,
      notes,
      private_notes,
    } = this.props;

    const tags = configs.tags
      ? Object.entries(configs.tags).map(([key, label]) => ({
          value: key,
          label,
        }))
      : [];

    const rightLabel = tags.some(tag => tag.value === right_label.key) ? right_label : {}

    return (
      <Form
        className="OfferValue"
        labelCol={{
          xs: { span: 24 },
          sm: { span: 8 },
        }}
        wrapperCol={{
          xs: { span: 24 },
          sm: { span: 10 },
        }}
      >
        <Form.Item
          label={messages.brokers.title}
          help={this.state.brokersError
            ? messages.brokers.errorMessage
            : messages.brokers.defaultMessage
          }
          required
        >
          <Select
            name={messages.brokers.name}
            value={broker}
            onChange={this.handleOnBrokerChange}
          >
            {
              Object.keys(brokers).map(brokerCode => <Select.Option value={brokerCode} key={brokerCode}>{brokers[brokerCode]}</Select.Option>)
            }
          </Select>
        </Form.Item>

        {products &&
          <Form.Item
            label={messages.products.title}
            help={this.state.productsError
              ? messages.products.errorMessage
              : messages.products.defaultMessage
            }
            required
          >
            <Select
              name={messages.products.name}
              value={product !== null ? `${product}` : null}
              onChange={this.handleOnProductChange}
              disabled={products.hasOwnProperty('size') || !broker}
            >
              {
                Object.keys(products).map(productId => <Select.Option value={productId} key={productId}>{products[productId]}</Select.Option>)
              }
            </Select>
          </Form.Item>
        }

        <Form.Item
          label={messages.price.title}
          help={this.state.monthlyRateError
            ? messages.price.errorMessage
            : messages.price.defaultMessage
          }
          required
          validateStatus={this.state.monthlyRateError ? 'error' : null}
        >
          <Input
            name={messages.price.name}
            value={isNaN(monthlyRate) ? '' : monthlyRate}
            suffix="€"
            onChange={this.handleOnMonthlyRateChange}
            minLength={2}
            maxLength={4}
          />
        </Form.Item>

        <Form.Item
          label={messages.deposit.title}
          help={this.state.depositError
            ? messages.deposit.errorMessage
            : messages.deposit.defaultMessage
          }
          required
          validateStatus={this.state.depositError ? 'error' : null}
        >
          <Input
            name={messages.deposit.name}
            value={isNaN(deposit) ? '' : deposit}
            suffix="€"
            onChange={this.handleOnDepositChange}
            minLength={1}
            maxLength={5}
          />
        </Form.Item>

        <Form.Item
          label={messages.duration.title}
          help={this.state.durationError
            ? messages.duration.errorMessage
            : messages.duration.defaultMessage
          }
          required
          validateStatus={this.state.durationError ? 'error' : null}
        >
          <Input
            name={messages.duration.name}
            value={isNaN(duration) ? '' : duration}
            suffix={messages.duration.unit}
            onChange={this.handleOnDurationChange}
            minLength={2}
            maxLength={2}
          />
        </Form.Item>

        <Form.Item
          label={messages.distance.title}
          help={this.state.distanceError
            ? messages.distance.errorMessage
            : messages.distance.defaultMessage
          }
          required
          validateStatus={this.state.distanceError ? 'error' : null}
        >
          <Input
            name={messages.distance.name}
            value={distance || ''}
            suffix={messages.distance.unit}
            onChange={this.handleOnDistanceChange}
            minLength={4}
            maxLength={6}
          />
        </Form.Item>

        <p style={{height: '22px'}}></p>

        <Form.Item
          label="Tempi di consegna"
          help="Indica i tempi di consegna previsti"
        >
          <Select
            name="delivery_time"
            value={deliveryTime || ''}
            onChange={this.handleOnDeliveryTimeChange}
          >
            <Select.Option value=" ">Seleziona i tempi di consegna</Select.Option>
            {deliveryTimes.hasOwnProperty('size') ? null :
              Object.values(deliveryTimes).map(monthYear => <Select.Option value={monthYear} key={monthYear}>{monthYear}</Select.Option>)
            }
          </Select>
        </Form.Item>

        <Form.Item
          label="Consegna veloce"
        >
          <Checkbox checked={fastDelivery} name="fast_delivery" onChange={this.handleOnFastDeliveryChange}>Sì</Checkbox>
        </Form.Item>

        <Form.Item
          label="Tipologia cliente"
          help="Seleziona la tipologia di cliente a cui l’offerta è rivolta"
        >
          <Select
            name="right_label"
            value={rightLabel.key || ''}
            onChange={this.handleOnRightLabelChange}
          >
            <Select.Option value=" ">Seleziona tipologia cliente</Select.Option>
            {tags.map(tag => <Select.Option value={tag.value} key={tag.value}>{tag.label}</Select.Option>)}
          </Select>
        </Form.Item>

        <Form.Item
          label="Targa o identificativo"
          help="Aggiungi un identificativo per questa offerta"
        >
          <Input
            name="reference_code"
            value={referenceCode || ''}
            onChange={this.handleOnReferenceChange}
          />
        </Form.Item>

        <Form.Item
          label="Informazioni aggiuntive"
          help="Aggiungi eventuali note che verranno visualizzate nella sezione “Note” dell’offerta e nel preventivo stampabile"
        >
          <Input.TextArea
            name="notes"
            value={notes}
            onChange={this.handleOnNotesChange}
          >
          </Input.TextArea>
        </Form.Item>

        <Form.Item
          label="Note per l'agente"
          help="Aggiungi informazioni utili alla tua rete vendita visibili solamente all’interno dell'offerta nella sezione “Dati Venditore”"
        >
          <Input.TextArea
            name="private_notes"
            value={private_notes}
            onChange={this.handleOnPrivateNotesChange}
          >
          </Input.TextArea>
        </Form.Item>
      </Form>
    );
  }
}

OfferValue.defaultProps = {};
OfferValue.propTypes = {
  setMonthlyRate: PropTypes.func.isRequired,
  hasError: PropTypes.func.isRequired,
  setDeposit: PropTypes.func.isRequired,
  setDuration: PropTypes.func.isRequired,
  setDistance: PropTypes.func.isRequired,
  setReference: PropTypes.func.isRequired,
  setNotes: PropTypes.func.isRequired,
  setPrivateNotes: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  monthlyRate: makeSelectMonthlyRate(),
  distance: makeSelectDistance(),
  duration: makeSelectDuration(),
  deposit: makeSelectDeposit(),
  right_label: makeSelectRightLabel(),
  fastDelivery: makeSelectFastDelivery(),
  deliveryTime: makeSelectDeliveryTime(),
  deliveryTimes: makeSelectDeliveryTimes(),
  broker: makeSelectBroker(),
  brokers: makeSelectBrokers(),
  products: makeSelectProducts(),
  product: makeSelectProduct(),
  referenceCode: makeSelectReference(),
  notes: makeSelectNotes(),
  private_notes: makeSelectPrivateNotes(),
});

export function mapDispatchToProps(dispatch) {
  return {
    loadDeliveryTimes: () => dispatch(loadDeliveryTimes()),
    loadBrokers: () => dispatch(loadBrokers()),
    loadProducts: () => dispatch(loadProducts()),
    setMonthlyRate: value => dispatch(setMonthlyRate(value)),
    setDeposit: value => dispatch(setDeposit(value)),
    setDuration: value => dispatch(setDuration(value)),
    setDistance: value => dispatch(setDistance(value)),
    setNotes: value => dispatch(setNotes(value)),
    setPrivateNotes: value => dispatch(setPrivateNotes(value)),
    setRightLabel: (key, value) => dispatch(setRightLabel(key, value)),
    setDeliveryTime: value => dispatch(setDeliveryTime(value)),
    setBroker: value => dispatch(setBroker(value)),
    setProduct: value => dispatch(setProduct(value)),
    setFastDelivery: value => dispatch(setFastDelivery(value)),
    setReference: value => dispatch(setReference(value)),
    hasError: value => dispatch(hasError(value)),
  };
}

const withReducer = injectReducer({ key: 'offerValue', reducer });
const withSaga = injectSaga({ key: 'offerValue', saga });

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);
export default compose(
  withSaga,
  withReducer,
  withConnect,
)(OfferValue);
